import config from 'login/config/index.js';
import axios from 'axios';

import { WindowProxy, ShowroomPublicTokenService } from '@sm360/widget-commons-core-services';
import { CookieService, UtilsService, COOKIES, AccountService } from '@sm360/widget-commons-general';

import FacebookAuthenticationService from 'login/services/FacebookAuthenticationService/FacebookAuthenticationService.js';
import AuthenticationService from 'login/services/AuthenticationService/AuthenticationService.js';

import EVENTS from 'login/constants/events.js';

/* eslint no-console:0  */

function minutesToMs(minutes) {
    return minutes * 60 * 1000;
}

class TokenService {
    constructor() {
        this.refreshTokenTimeout = null;
        this.baseUrl = `${config.endpoint.account}/accounts/token/refresh`;

        this.facebookAuthenticate = this.facebookAuthenticate.bind(this);
        this.setUserToken = this.setUserToken.bind(this);
        this.getUserToken = this.getUserToken.bind(this);
        this.isFacebookToken = this.isFacebookToken.bind(this);
        this.extractTokenPayload = this.extractTokenPayload.bind(this);
        this.initRefreshTokenJob = this.initRefreshTokenJob.bind(this);
        this.initialTokenValidation = this.initialTokenValidation.bind(this);
        this.refreshTokenDelay = this.refreshTokenDelay.bind(this);
        this.getNextRefreshTokenDelayInMS = this.getNextRefreshTokenDelayInMS.bind(this);
        this.refreshTokenFacebook = this.refreshTokenFacebook.bind(this);
        this.facebookTokenRefreshSequence = this.facebookTokenRefreshSequence.bind(this);
        this.facebookAuthenticate = this.facebookAuthenticate.bind(this);
        this.refreshTokenSM360 = this.refreshTokenSM360.bind(this);
        this.refreshToken = this.refreshToken.bind(this);
    }

    setUserToken(token, refreshToken) {
        const $window = WindowProxy.get();

        CookieService.setToken(COOKIES.ACCOUNT.AUTHENTICATE_TOKEN, token);
        CookieService.setToken(COOKIES.ACCOUNT.REFRESH_TOKEN, refreshToken);

        if ($window) {
            $window.dispatchEvent(new $window.CustomEvent(EVENTS.AUTHENTICATED));
        }
    }

    getUserToken() {
        const authToken = CookieService.getToken(COOKIES.ACCOUNT.AUTHENTICATE_TOKEN);
        return authToken ? Promise.resolve(authToken) : ShowroomPublicTokenService.getToken();
    }

    isFacebookToken() {
        const token = CookieService.getToken(COOKIES.ACCOUNT.AUTHENTICATE_TOKEN);
        if (token) {
            const tokenPayload = this.extractTokenPayload(token);
            return !!tokenPayload.FACEBOOK_USER_TOKEN;
        }

        return false;
    }

    extractTokenPayload(token) {
        const dot = '.';
        const tokenParts = token.split(dot);
        const tokenPayload = tokenParts[1] || '';

        try {
            return JSON.parse(UtilsService.decodeBase64(tokenPayload));
        } catch (err) {
            Error('JSON parse error: ', err);
            return '';
        }
    }

    initRefreshTokenJob() {
        const token = CookieService.getToken(COOKIES.ACCOUNT.AUTHENTICATE_TOKEN);

        if (token) {
            this.refreshTokenDelay();
        }

        return Promise.resolve();
    }

    initialTokenValidation() {
        const token = CookieService.getToken(COOKIES.ACCOUNT.AUTHENTICATE_TOKEN);
        const fbAPI = WindowProxy.getFBApi();

        if (token) {
            const tokenPayload = UtilsService.extractTokenPayload(token);

            if (tokenPayload && tokenPayload.FACEBOOK_USER_TOKEN && fbAPI) {
                fbAPI.getLoginStatus((response) => {
                    if (response.status !== 'connected') {
                        AuthenticationService.logout();
                    }
                });
            } else if (!tokenPayload) {
                AuthenticationService.logout();
            }
        }
    }

    refreshTokenDelay() {
        const token = CookieService.getToken(COOKIES.ACCOUNT.AUTHENTICATE_TOKEN);
        const refreshToken = CookieService.getToken(COOKIES.ACCOUNT.REFRESH_TOKEN);

        if (AccountService.isAuthenticated()) {
            if (token && refreshToken && refreshToken !== 'null') {
                const tokenPayload = UtilsService.extractTokenPayload(token);
                const nextRefreshDelayInMS = this.getNextRefreshTokenDelayInMS(tokenPayload);
                clearTimeout(this.refreshTokenTimeout);

                if (tokenPayload.FACEBOOK_USER_TOKEN) {
                    if (nextRefreshDelayInMS > 0) {
                        this.refreshTokenFacebook(nextRefreshDelayInMS);
                    } else {
                        this.facebookTokenRefreshSequence();
                    }
                } else if (nextRefreshDelayInMS > 0) {
                    this.refreshTokenSM360(token, refreshToken, nextRefreshDelayInMS);
                } else {
                    this.refreshToken(token, refreshToken);
                }
            } else {
                AuthenticationService.logout();
            }
        }
    }

    /**
     * Calculate the delay we need to wait before refreshing the token
     * @param {Object} tokenPayload
     * @returns {Number} Number of milliseconds to wait before refreshing the token
     */
    getNextRefreshTokenDelayInMS(tokenPayload) {
        const currentDate = new Date();
        const currentTimeStampInMs = currentDate.getTime();

        // Removing 6 minutes of the expiration to give us a grace period
        const tokenExpirationMinus5MinInMs = tokenPayload.exp * 1000 - minutesToMs(4);

        if (tokenExpirationMinus5MinInMs < currentTimeStampInMs) {
            // In the grace period, need to refresh now
            return 0;
        }

        // Refresh in X ms where X is the nb of ms between now and the expiration time
        return tokenExpirationMinus5MinInMs - currentTimeStampInMs;
    }

    refreshTokenFacebook(nextRefreshDelayInMS) {
        this.refreshTokenTimeout = setTimeout(this.facebookTokenRefreshSequence, nextRefreshDelayInMS);
    }

    facebookTokenRefreshSequence() {
        const fbAPI = WindowProxy.getFBApi();

        if (fbAPI) {
            fbAPI.getLoginStatus((response) => {
                if (response.status === 'connected') {
                    console.log('Connected to facebook -> authenticate again');

                    this.facebookAuthenticate(response.authResponse.accessToken);
                } else {
                    fbAPI.login(
                        (loginResponse) => {
                            console.log('Re-authenticate to facebook -> done');
                            console.log(loginResponse.authResponse);

                            if (loginResponse.authResponse) {
                                this.facebookAuthenticate(loginResponse.authResponse.accessToken);
                            } else {
                                AuthenticationService.logout();
                            }
                        },
                        {
                            scope: 'email',
                        }
                    );
                }
            });
        }
    }

    facebookAuthenticate(token) {
        return FacebookAuthenticationService.authenticate(token).then(() => {
            this.refreshTokenDelay();
        });
    }

    refreshTokenSM360(token, refreshToken, nextRefreshDelayInMS) {
        this.refreshTokenTimeout = setTimeout(() => {
            this.refreshToken(token, refreshToken);
        }, nextRefreshDelayInMS);
    }

    refreshToken(token, refreshToken) {
        const tokenSetRequest = {
            token,
            refreshToken,
        };

        return axios.post(this.baseUrl, tokenSetRequest).then((response) => {
            this.setUserToken(response.data.token, response.data.refreshToken);
            this.refreshTokenDelay();
        });
    }
}

export default new TokenService();
